package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.model.pages.BagsUIState
import com.example.mystybag.appCore.model.pages.BagSegment
import com.example.mystybag.appCore.model.pages.BagListItem
import com.example.mystybag.appCore.model.pages.BagDetails
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_IS_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_CAN_CREATE_BAGS
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_LOCATION_IDS
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.appCore.logic.NavigatorAddBag
import com.example.mystybag.core.defines.UtilsDateDefines.DATE_FORMAT_YYYY_DASH_MM_DASH_DD
import com.example.mystybag.core.defines.UtilsDateDefines.DATE_YYYY_MM_DD_T_HH_MM_SS
import com.example.mystybag.core.expected.DateTimeHelper
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.sharedmodels.project.responseModels.merchant.BagProductSelection
import com.example.mystybag.sharedmodels.project.responseModels.merchant.BagProductSelectionMode
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantBagsRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.UpdateBagRequestModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.GetBagDetailsResponseModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantBagsResponseModel
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

class BagsViewModel(context: AppContext): ChildViewModel<BagsUIState>(context, BagsUIState()) {

    override fun onCreate() {
        navControls.title = sharedString(SR.strings.bags)
        loadBagsData()
    }
    
    override fun onResume() {
        // Refresh bags data when page becomes visible again
        // This ensures the list is updated after creating a new bag
        loadBagsData()
    }

    fun loadBagsData() {
        val merchantId = Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)
        if (merchantId == 0L) {
            showSnackbar(sharedString(SR.strings.please_log_in_again))
            return
        }

        scope.launch {
            updateState { it.copy(isLoading = true) }
            
            commandBlock(showProgress = false, hideProgress = false) {
                CommonCommands.getMerchantBags(
                    GetMerchantBagsRequestModel(
                        merchantId = merchantId,
                        locationId = uiState.value.selectedLocationId,
                        segment = null
                    )
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val responseModel = response as? GetMerchantBagsResponseModel
                                var locations = responseModel?.locations ?: emptyList()
                                val isStaff = Preferences.getBoolean(key = PREFERENCES_IS_STAFF, false)
                                val staffLocationIds = if (isStaff) {
                                    Preferences.getString(key = PREFERENCES_STAFF_LOCATION_IDS, "")
                                        .split(",").mapNotNull { it.trim().toLongOrNull() }.filter { it > 0L }
                                } else emptyList()
                                if (staffLocationIds.isNotEmpty()) {
                                    locations = locations.filter { it.id in staffLocationIds }
                                }
                                val mapped = responseModel?.bags?.map { bag ->
                                    BagListItem(
                                        bagId = bag.id ?: 0L,
                                        locationId = bag.merchantLocationId ?: 0L,
                                        locationName = bag.locationName ?: "",
                                        title = bag.title,
                                        availableCount = bag.availableCount ?: 0,
                                        totalCount = bag.totalCount ?: 0,
                                        price = bag.priceWithDiscount ?: 0.0,
                                        currency = bag.currency ?: "",
                                        activeTo = bag.activeTo,
                                        activeFrom = bag.activeFrom,
                                        activeFromFormatted = formatDateForDisplay(bag.activeFrom),
                                        activeToFormatted = formatDateForDisplay(bag.activeTo),
                                        createdOnFormatted = formatCreatedOn(bag.createdAt)
                                    )
                                } ?: emptyList()
                                val locationFiltered = if (staffLocationIds.isNotEmpty()) {
                                    mapped.filter { it.locationId in staffLocationIds }
                                } else mapped
                                val segment = uiState.value.selectedSegment
                                val displayed = filterBagsBySegment(locationFiltered, segment)
                                val canCreate = !isStaff ||
                                    Preferences.getBoolean(key = PREFERENCES_STAFF_CAN_CREATE_BAGS, false)
                                updateState {
                                    it.copy(
                                        isLoading = false,
                                        locations = locations,
                                        allBags = locationFiltered,
                                        bags = displayed,
                                        canCreateBags = canCreate
                                    )
                                }
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(com.example.mystybag.communication.utils.Response.getErrorMessage(response.status, response.apiEndPoint, response.statusMessage))
                            }
                        }
                    )
            }
        }
    }
    
    fun selectLocation(locationId: Long?) {
        updateState { it.copy(selectedLocationId = locationId) }
        loadBagsData()
    }
    
    fun selectSegment(segment: BagSegment) {
        updateState {
            it.copy(
                selectedSegment = segment,
                bags = filterBagsBySegment(it.allBags, segment)
            )
        }
    }
    
    fun selectBag(bagId: Long) {
        val bag = uiState.value.bags.find { it.bagId == bagId }
        if (bag != null) {
            // Load bag details
            scope.launch {
                commandBlock(showProgress = false, hideProgress = false) {
                    CommonCommands.getBagDetails(bagId)
                        .onResponse(
                            success = { response ->
                                launchOnMain {
                                    val responseModel = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.GetBagDetailsResponseModel
                                    val bagDetails = BagDetails(
                                        bagId = responseModel?.bagId ?: 0L,
                                        locationId = responseModel?.locationId ?: 0L,
                                        locationName = responseModel?.locationName ?: "",
                                        title = "aaas",//responseModel?.title,
                                        availableCount = responseModel?.availableCount ?: 0,
                                        reservedCount = responseModel?.reservedCount ?: 0,
                                        totalCount = responseModel?.totalCount ?: 0,
                                        priceNormal = responseModel?.priceNormal ?: 0.0,
                                        priceWithDiscount = responseModel?.priceWithDiscount ?: 0.0,
                                        currency = responseModel?.currency ?: "",
                                        activeFrom = responseModel?.activeFrom,
                                        activeTo = responseModel?.activeTo,
                                        createdAt = responseModel?.createdAt,
                                        allergens = responseModel?.allergens ?: emptyList()
                                    )
                                    updateState { it.copy(selectedBagDetails = bagDetails) }
                                }
                            },
                            baseError = { response ->
                                // Handle error
                            }
                        )
                }
            }
        }
    }
    
    fun navigateToAddBag(locationId: Long) {
        if (!uiState.value.canCreateBags) return
        val location = uiState.value.locations.find { it.id == locationId }
        navigate(NavigatorAddBag(
            merchantLocationId = locationId,
            businessCategoryId = location?.businessCategoryId,
            bagId = null
        ))
    }

    fun navigateToEditBag(bag: BagListItem) {
        // Staff without bag-edit permission can only view bags – same flag gates Add and Edit.
        if (!uiState.value.canCreateBags) return
        // Ended/expired bags are read-only – there's no edit screen for them.
        if (uiState.value.selectedSegment != BagSegment.ACTIVE) return
        val location = uiState.value.locations.find { it.id == bag.locationId }
        navigate(NavigatorAddBag(
            merchantLocationId = bag.locationId,
            businessCategoryId = location?.businessCategoryId,
            bagId = bag.bagId
        ))
    }
    
    fun viewReservations(bagId: Long) {
        // Navigate to reservations view for this bag
        // This would need a NavigatorReservationsForBag or similar
    }
    
    fun clearSelectedBag() {
        updateState { it.copy(selectedBagDetails = null) }
    }

    fun onDeactivateBagClick(bagId: Long) {
        // Deactivation is an edit-like action – disallowed for staff without bag-edit permission.
        if (!uiState.value.canCreateBags) return
        updateState { it.copy(bagIdPendingDeactivate = bagId) }
    }

    fun onDismissDeactivateBagDialog() {
        updateState { it.copy(bagIdPendingDeactivate = null) }
    }

    fun onConfirmDeactivateBag() {
        val bagId = uiState.value.bagIdPendingDeactivate ?: return
        updateState { it.copy(bagIdPendingDeactivate = null) }
        scope.launch {
            commandBlock(showProgress = true) {
                CommonCommands.getBagDetails(bagId)
                    .onResponse(
                        success = { response ->
                            val details = response as? GetBagDetailsResponseModel
                            if (details == null) {
                                launchOnMain {
                                    showSnackbar(sharedString(SR.strings.an_error_occurred_please_try_again_later))
                                }
                                return@onResponse
                            }
                            if (details.isEnabled == false) {
                                launchOnMain { loadBagsData() }
                                return@onResponse
                            }
                            val updateModel = buildUpdateBagDeactivateRequest(details) ?: run {
                                launchOnMain {
                                    showSnackbar(sharedString(SR.strings.an_error_occurred_please_try_again_later))
                                }
                                return@onResponse
                            }
                            CommonCommands.updateBag(updateModel)
                                .onResponse(
                                    success = {
                                        launchOnMain {
                                            showSnackbar(sharedString(SR.strings.deactivate_bag_success))
                                            loadBagsData()
                                        }
                                    },
                                    baseError = { err ->
                                        launchOnMain {
                                            showSnackbar(
                                                Response.getErrorMessage(
                                                    err.status,
                                                    err.apiEndPoint,
                                                    err.statusMessage
                                                )
                                            )
                                        }
                                    }
                                )
                        },
                        baseError = { err ->
                            launchOnMain {
                                showSnackbar(
                                    Response.getErrorMessage(err.status, err.apiEndPoint, err.statusMessage)
                                )
                            }
                        }
                    )
            }
        }
    }

    private fun parseBagLocalDate(s: String?): LocalDate? {
        if (s.isNullOrBlank()) return null
        return try {
            LocalDate.parse(s.take(10))
        } catch (_: Exception) {
            null
        }
    }

    /** Builds [UpdateBagRequestModel] from bag details with [UpdateBagRequestModel.isEnabled] = false. */
    private fun buildUpdateBagDeactivateRequest(details: GetBagDetailsResponseModel): UpdateBagRequestModel? {
        val id = details.bagId ?: return null
        val locationId = details.locationId ?: return null
        val activeFrom = parseBagLocalDate(details.activeFrom) ?: return null
        val activeTo = parseBagLocalDate(details.activeTo) ?: return null
        val bagsCount = details.totalCount ?: return null
        val priceNormal = details.priceNormal ?: return null
        val priceWithDiscount = details.priceWithDiscount ?: return null
        val currency = details.currency ?: return null
        val sameDay = activeFrom == activeTo
        val activeFromDateTime = LocalDateTime(
            year = activeFrom.year,
            monthNumber = activeFrom.monthNumber,
            dayOfMonth = activeFrom.dayOfMonth,
            hour = 0,
            minute = 0
        )
        val activeToDateTime = LocalDateTime(
            year = activeTo.year,
            monthNumber = activeTo.monthNumber,
            dayOfMonth = activeTo.dayOfMonth,
            hour = if (sameDay) 23 else 0,
            minute = if (sameDay) 59 else 0
        )
        val pickupFrom = details.pickupTimeFrom?.trim()?.takeIf { it.isNotEmpty() }
        val pickupTo = details.pickupTimeTo?.trim()?.takeIf { it.isNotEmpty() }
        // Preserve the bag's existing product selection so deactivating doesn't reset
        // its products (and the server-derived allergens that come from them).
        val productSelection = BagProductSelection(
            mode = details.productSelectionMode,
            categoryId = details.productCategoryId,
            productIds = if (details.productSelectionMode == BagProductSelectionMode.CUSTOM) {
                details.products.map { it.id }
            } else {
                emptyList()
            }
        )
        return UpdateBagRequestModel(
            bagId = id,
            merchantLocationId = locationId,
            title = details.title?.trim()?.takeIf { it.isNotEmpty() },
            activeFrom = activeFromDateTime,
            activeTo = activeToDateTime,
            pickupTimeFrom = pickupFrom,
            pickupTimeTo = pickupTo,
            bagsCount = bagsCount,
            priceNormal = priceNormal,
            priceWithDiscount = priceWithDiscount,
            isEnabled = false,
            currency = currency,
            productSelection = productSelection
        )
    }

    /** Format for card "Active from" / date-only display, e.g. "23 Jan 2026". */
    private fun formatDateForDisplay(isoDate: String?): String {
        if (isoDate.isNullOrBlank()) return "-"
        return try {
            if (isoDate.length >= 10) {
                DateTimeHelper.formatDate(isoDate.take(10), DATE_FORMAT_YYYY_DASH_MM_DASH_DD, "dd MMM yyyy")
            } else isoDate
        } catch (_: Exception) {
            isoDate
        }
    }

    /** Format for card "Created on:" line, e.g. "23 Jan 2026, 12:30". */
    private fun formatCreatedOn(createdAt: String?): String {
        if (createdAt.isNullOrBlank()) return "-"
        return try {
            when {
                createdAt.length >= 19 && createdAt[10] == 'T' ->
                    DateTimeHelper.formatDate(createdAt.take(19), DATE_YYYY_MM_DD_T_HH_MM_SS, "dd MMM yyyy, HH:mm")
                createdAt.length >= 10 ->
                    DateTimeHelper.formatDate(createdAt.take(10), DATE_FORMAT_YYYY_DASH_MM_DASH_DD, "dd MMM yyyy")
                else -> createdAt
            }
        } catch (_: Exception) {
            createdAt
        }
    }

    private fun filterBagsBySegment(bags: List<BagListItem>, segment: BagSegment): List<BagListItem> {
        val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        return when (segment) {
            BagSegment.ACTIVE -> bags.filter { isBagActive(it.activeTo, today) }
            BagSegment.ENDED -> bags.filter { !isBagActive(it.activeTo, today) }
        }
    }

    private fun isBagActive(activeTo: String?, today: LocalDate): Boolean {
        if (activeTo.isNullOrBlank()) return true
        val endDate = parseActiveToDate(activeTo) ?: return true
        return endDate >= today
    }

    private fun parseActiveToDate(activeTo: String): LocalDate? = try {
        LocalDate.parse(activeTo.take(10))
    } catch (_: Exception) {
        null
    }
}

